<template>
  <nav
    class="super-navbar !visible lg:flex-grow basis-[100%] xl:!flex xl:basis-auto text-paragraph overflow-auto items-stretch"
    :class="showSuperMenu ? ' mobile' : ' hidden'"
    aria-label="Top Navigation"
  >
    <ul
        id="superNav"
        class="super-navbar-nav list-style-none flex flex-col pl-0 lg:flex-row justify-between items-stretch"
    >
      <li class="nav-item">
        <NuxtLink
          :external="external" :to="'/'"
          class="nav-link lg:hover:text-primary whitespace-nowrap flex items-center lg:cursor-default justify-between px-2 py-4 relative hover:flyout-indicator indicator-primary h-full"
          :class="{ 'flyout-indicator': openSuperDropdown === '0'}"
          title="Unsere Produkte">
          <template v-if="showSuperMenu">
            <div
              class="flex-initial w-full"
              @click.prevent="emit('showMegaMenu'); openSuperDropdown == '1' ? (openSuperDropdown = '0'): (openSuperDropdown = '1')">Unsere Produkte</div>
          </template>
          <template v-else>Unsere Produkte</template>
          <span
            class="lg:ml-1 px-4 lg:px-0 duration-300 ease-in-out lg:hidden"
            :class="openSuperDropdown == '1' ? 'rotate-180 text-primary' : ''"
            @click.prevent="emit('showMegaMenu'); openSuperDropdown == '1' ? (openSuperDropdown = '0'): (openSuperDropdown = '1')"
            ><BasicIcon :name="'chevron-down'" icon-class="w-6 h-6" />
          </span>
        </NuxtLink>
        <slot name="megamenu"/>
      </li>
      <li
        v-for="superCategory in superCategoryTree" :key="superCategory?.id || superCategory?.extId" class="nav-item"
        :class="'group'">
        <NuxtLink
          :id="'dropdownMenu_' + superCategory?.id"
          :external="external"
          class="nav-link whitespace-nowrap flex items-center justify-between px-2 py-4 h-full lg:hover:text-primary" :class="superCategory?.descendants?.length > 0 ? ' dropdown' : ''"
          target="_self" :to="superCategory?.url" :title="superCategory?.alt">
          {{ superCategory?.name }}
          <template v-if="superCategory?.descendants">
            <span
              v-if="superCategory?.descendants?.length > 0"
              class="w-5 lg:ml-2 lg:hidden duration-300 ease-in-out"
              :class="openSuperDropdown == superCategory?.id ? 'rotate-90' : ''"
              @click.prevent="openSuperDropdown == superCategory?.id ? (openSuperDropdown = '0') : (openSuperDropdown = superCategory?.id)">
              <BasicIcon :name="'chevron-right'" class="w-6 h-6 lg:hidden" />
            </span>
          </template>
        </NuxtLink>
          <template v-if="superCategory?.descendants && superCategory?.descendants?.length > 0">
            <div
              :id="'dropdownContainer_' + superCategory?.id"
              class="dropdown-container lg:container lg:mx-auto lg:absolute pl-2 lg:pl-0 transition invisible duration-500 ease-in-out group-hover:transform transform right-0 left-0 group-hover:opacity-100 group-hover:visible _z-10 top-full"
              :class="openSuperDropdown == superCategory?.id ? ' !visible opacity-1' : ' opacity-0 hidden xl:block'">
              <template v-if="superCategory?.descendants">
                <ul class="navbar-nav subnav-layer list-style-none flex flex-col pl-0 lg:flex-row">
                  <template 
                    v-for="superChild in superCategory?.descendants"
                    :key="superChild.id || superChild?.extId">
                    <li class="nav-item">
                      <NuxtLink
                        :external="external" class="nav-link px-2 py-4 lg:px-3 lg:py-5" target="_self"
                        :to="superChild.url" :title="superChild.name">{{ superChild.name }}</NuxtLink>
                    </li>
                  </template>
                </ul>
              </template>
            </div>
          </template>
      </li>
      <template v-if="superMenuItemLength < 5">
        <!-- FIXME: Remove hardcoded filter-->
        <li v-for="campaign in campaignMenuItems?.topLevelItems?.concat(campaignMenuItems?.items)?.filter(cam => cam?.name == 'Outlet-Center')" :key="campaign.name" class="nav-item group">
          <NuxtLink class="nav-link whitespace-nowrap flex items-center justify-between px-2 py-4 h-full" :to="campaign?.url" :title="campaign?.alt">
            {{ campaign?.name }}
          </NuxtLink>
        </li>
      </template>
      <template v-else>
        <template v-if="campaignMenuItems?.topLevelItems?.filter(cam => cam?.name == 'Outlet-Center').length < 2">
          <li 
            v-for="campaign in campaignMenuItems?.topLevelItems?.filter(cam => cam?.name == 'Outlet-Center')" 
            :key="campaign.name" class="nav-item group">
            <NuxtLink class="nav-link whitespace-nowrap flex items-center justify-between px-2 py-4 h-full" :to="campaign?.url" :title="campaign?.alt">
              {{ campaign?.name }}
            </NuxtLink>
          </li>
          </template>
      </template>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { useSuperMenuState, useSuperMenuDropdownState } from "~/composables/states";
import type { MenuLink } from "~~/models/Navigation";
import type { NeoCampaignGroup } from "@expert/types";
import { mapFromCampaignGroup } from "~/composables/Mapper/MenuLink";

interface CampaignMenu {
  topLevelItems: Array<MenuLink>;
  items: Array<MenuLink>;
  totalLength: number;
}

interface Props {
  superCategoryTree: Array<MenuLink>;
  external?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  external: false,
});

const emit = defineEmits(['showMegaMenu']);

const openSuperDropdown = useSuperMenuDropdownState();
const showSuperMenu = useSuperMenuState();

const campaignQuery = computed(() => {
  return {
    storeId: useCurrentStore().value?.id,
  };
});

const { data: campaignGroupsForStore } = await useFetch<Array<NeoCampaignGroup>>(getApiUrl() + '/api/promotions/campaigns/groups/rootGroups', {
  query: campaignQuery,
  server: false,
});

const campaignMenuItems = computed<CampaignMenu>(() => {
  const campaignGroupMenuItems: Array<MenuLink> = campaignGroupsForStore?.value?.sort((firstGroup, secondGroup) => secondGroup.score - firstGroup.score).map(mapFromCampaignGroup) ?? [];
  const topLevelItems = campaignGroupMenuItems?.filter(menuItem => menuItem?.topLevelMenu) ?? [];
  const items = campaignGroupMenuItems?.filter(menuItem => !menuItem?.topLevelMenu) ?? [];

  return {
    topLevelItems,
    items,
    totalLength: topLevelItems.length + items.length,
  } as CampaignMenu;
});

const superMenuItemLength = computed(() => {
  const superCategoryTreeLength = props?.superCategoryTree?.length ?? 0;
  const campaignItemsLength = campaignMenuItems?.value?.totalLength ?? 0;

  return superCategoryTreeLength + campaignItemsLength;
});
</script>
<style lang="postcss" scoped>
.super-navbar {
  scrollbar-width: thin;
  scrollbar-color: rgb(var(--color-primary)) #fff;
}
.navbar-nav.subnav-layer {
  @apply justify-start;
  &:has(:nth-child(5)) {
    @apply justify-between;
  }
}
</style>